import { useEffect } from 'react';
import { useDocumentPreview, useDownloadDocument } from '../../api/bewerber';
import { Dokument, DokumentTypConfig, isImageMimeType, isPdfMimeType } from '../../types/bewerber';
import { PdfViewer } from './PdfViewer';
import { ImageViewer } from './ImageViewer';

interface DocumentPreviewModalProps {
  document: Dokument;
  bewerberId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function DocumentPreviewModal({
  document: doc,
  bewerberId,
  isOpen,
  onClose,
}: DocumentPreviewModalProps) {
  const { data: previewUrl, isLoading, error } = useDocumentPreview(
    bewerberId,
    doc.id,
    isOpen,
  );
  const downloadMutation = useDownloadDocument();

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleDownload = () => {
    downloadMutation.mutate({
      bewerberId,
      documentId: doc.id,
      filename: doc.originalFilename,
    });
  };

  if (!isOpen) return null;

  const isImage = isImageMimeType(doc.mimeType);
  const isPdf = isPdfMimeType(doc.mimeType);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
        onClick={onClose}
      />

      {/* Modal - 60% width, centered */}
      <div className="fixed top-8 bottom-8 left-1/2 -translate-x-1/2 w-[60%] max-w-4xl flex flex-col bg-white rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-medium text-gray-900 truncate">
              {doc.originalFilename}
            </h3>
            <p className="text-sm text-gray-500">
              {DokumentTypConfig[doc.typ]?.label || doc.typ} •{' '}
              {(doc.fileSize / 1024).toFixed(1)} KB •{' '}
              {new Date(doc.uploadedAt).toLocaleDateString('de-DE')}
            </p>
          </div>

          <div className="flex items-center gap-2 ml-4">
            {/* Download button */}
            <button
              onClick={handleDownload}
              disabled={downloadMutation.isPending}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              title="Herunterladen"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {downloadMutation.isPending ? 'Lädt...' : 'Download'}
            </button>

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              title="Schließen (Esc)"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-gray-500">Dokument wird geladen...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <svg className="w-16 h-16 text-red-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="mt-4 text-gray-900 font-medium">Fehler beim Laden</p>
                <p className="mt-1 text-gray-500">Das Dokument konnte nicht geladen werden.</p>
              </div>
            </div>
          ) : previewUrl ? (
            <>
              {isPdf && <PdfViewer url={previewUrl} filename={doc.originalFilename} />}
              {isImage && <ImageViewer url={previewUrl} filename={doc.originalFilename} className="h-full" />}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
